import {useState} from "react";
import {Link} from 'react-router';
import {Button, Card, Row, Col, Table, Collapse, Divider} from "antd";
import {getPurchaseOrderReport, getPurchaseOrderLabor} from "../../api/Api";
import {handleNoSession} from "../../util/errorUtils";
import {toFSDate, toFSDateTime} from "../../util/timeutils";
import {BuyerSelectWidget} from "../sales/BuyerSelectWidget";
import {DatePickerWidget} from "../widget/DatePickerWidget";


export const PurchaseOrderReport = ({refresh, onRefresh, onApiFail}) => {
    const [startDate, setStartDate] = useState(undefined);
    // displayDate is important, we are working around a UTC to local timezone change
    // we need to feed DatePickerWidget a local time so that it works as expected, otherwise date is off by 1
    const [displayStartDate, setDisplayStartDate] = useState(undefined);

    const [endDate, setEndDate] = useState(undefined);
    const [displayEndDate, setDisplayEndDate] = useState(undefined);
    const [selectedBuyer, setSelectedBuyer] = useState(undefined);
    const [buyerValue, setBuyerValue] = useState(undefined);


    const [report, setReport] = useState({});
    // const [lineItems, setLineItems] = useState([]);
    const [disable, setDisable] = useState(false);
    const [numReports, setNumReports] = useState(0);
    const [laborMap, setLaborMap] = useState({});

    const onStartDateChange = (dateString, dateJS) => {
        setStartDate(dateString);

        const dateJsString = dateJS.toString();

        // we will set displayDate to a local time for DatePickerWidget
        setDisplayStartDate(dateJsString);
    }

    const onEndDateChange = (dateString, dateJS) => {
        setEndDate(dateString);

        const dateJsString = dateJS.toString();

        // we will set displayDate to a local time for DatePickerWidget
        setDisplayEndDate(dateJsString);
    }


    const onBuyerSelect = (val) => {
        setSelectedBuyer(val);
    }

    const runReport = () => {
        setDisable(true);
        let buyerId;
        if (selectedBuyer !== undefined) {
            buyerId = selectedBuyer.id;
        }
        getPurchaseOrderReport(startDate, endDate, buyerId).then(result => {
            const lines = result.map(r => {
                return {
                    ...r,
                    key: r.id
                }
            });
            // setLineItems(lines);

            // collate the line items into arrays based on purchase order
            const poMap = {}
            lines.forEach(l => {
                if (poMap[l.purchaseId] === undefined) {
                    poMap[l.purchaseId] = {
                        hours: 0,
                        wholesaleCost: 0,
                        testingCost: 0,
                        supplyCost: 0,
                        lines: []
                    };
                }
                poMap[l.purchaseId].lines.push(l)
                if (l.hours !== null) {
                    poMap[l.purchaseId].hours += parseFloat(l.hours);
                }
                if (l.wholesaleCost !== null) {
                    poMap[l.purchaseId].wholesaleCost += parseFloat(l.wholesaleCost);
                }
                if (l.supplyCost !== null) {
                    poMap[l.purchaseId].supplyCost += parseFloat(l.supplyCost);
                }
                if (l.testingCost !== null) {
                    poMap[l.purchaseId].testingCost += parseFloat(l.testingCost);
                }
            });
            setReport(poMap);
            // console.log(poMap);
            setNumReports(numReports + 1);

            // now we need to take all the Purchase Reports from the maps and get all the labor records for each PO
            const promises = [];
            for (const purchaseId of Object.keys(poMap)) {
                promises.push(getPurchaseOrderLabor(purchaseId));
            }
            return Promise.all(promises);
        }).then(resultsArray => {
            // each element in results array is an array for one of the Purchase Orders
            const nextLaborMap = {}
            resultsArray.forEach((laborArray, index) => {
                console.log(laborArray)
                if (laborArray.length > 0) {
                    // if there is at least one element, find the purchaseId for the first element and assign it into our map
                    const purchaseId = laborArray[0].purchaseId;
                    nextLaborMap[purchaseId] = laborArray.map(labor => {
                        return {
                            ...labor,
                            key: labor.id,
                        }
                    });
                }
            });
            setLaborMap(nextLaborMap);
        }).catch (e => {
            console.log(e);
            handleNoSession(e, onApiFail);
        }).finally(() => {
            setDisable(false);
        })
    }

    const clearForm = () => {
        setStartDate(undefined);
        setDisplayStartDate(undefined);
        setEndDate(undefined);
        setDisplayEndDate(undefined);
        setBuyerValue(undefined);
        setSelectedBuyer(undefined);
        setReport({});
        setLaborMap({});
    }


    const getReportHours = (purchaseId) => {
        const hours = report[purchaseId].hours.toFixed(2);
        return parseFloat(hours);
    }

    const getReportWholesale = (purchaseId) => {
        const wholesaleCost = report[purchaseId].wholesaleCost.toFixed(2);
        return parseFloat(wholesaleCost);
    }

    const getReportSupply = (purchaseId) => {
        const supplyCost = report[purchaseId].supplyCost.toFixed(2);
        return parseFloat(supplyCost);
    }

    const getReportTesting = (purchaseId) => {
        const testingCost = report[purchaseId].testingCost.toFixed(2);
        return parseFloat(testingCost);
    }

    const poColumns = [
        {
            title: 'Purchase Order',
            key: 'PurchaseOrder',
            dataIndex: 'poNumber',
            render: (val, record) => {
                return <Link to={`/purchaseorder/${record.purchaseId}`}>{val}</Link>
            }
        },
        {
            title: 'Buyer',
            key: 'buyer',
            dataIndex: 'firstname',
            render: (val, record) => {
                let fullName = record.firstname;
                if (record.lastname) fullName += record.lastname;
                return fullName;
            }
        },
        {
            title: 'Due Date',
            key: 'duedate',
            dataIndex: 'dueDate',
            render: (val) => {
                return toFSDate(new Date(val))
            },
            sorter: (a, b) => {
                const aDate = new Date(a.dueDate);
                const bDate = new Date(b.dueDate);
                return aDate - bDate;
            }
        },
        {
            title: 'Time Spent',
            key: 'hours',
            dataIndex: 'hours',
            render: (val, record, index) => {
                const lineItemHours = getReportHours(record.purchaseId)
                // add in the hours reported on the po itself
                let poHours = 0;
                if (laborMap[record.purchaseId] !== undefined) {
                    poHours = laborMap[record.purchaseId].reduce((acc, labor) => {
                        return acc + parseFloat(labor.hours);
                    }, 0);
                }
                const hours = parseFloat((lineItemHours + poHours).toFixed(2));
                return `${hours} hrs`
            },
            sorter: (a, b) => {
                const aHours = getReportHours(a.purchaseId);
                const bHours = getReportHours(b.purchaseId);
                return aHours - bHours;
            }
        },
        {
            title: 'Wholesale Cost',
            key: 'wholesale',
            dataIndex: 'wholesaleCost',
            render: (val, record) => {
                const wholesaleCost = getReportWholesale(record.purchaseId);
                return `$ ${wholesaleCost}`
            }
        },
        {
            title: 'Supply Cost',
            key: 'supply',
            dataIndex: 'supplyCost',
            render: (val, record) => {
                const supplyCost = getReportSupply(record.purchaseId);
                return `$ ${supplyCost}`
            }
        },
        {
            title: 'Testing Cost',
            key: 'testing',
            dataIndex: 'testingCost',
            render: (val, record) => {
                const testingCost = getReportTesting(record.purchaseId);
                return `$ ${testingCost}`
            }
        }
    ];

    const itemColumns = [
        {
            title: 'Product',
            key: 'product',
            dataIndex: 'name',
            render: (val, record) => {
                return val;
            }
        },
        {
            title: 'Hours',
            key: 'hours',
            dataIndex: 'hours',
            render: (val, record) => {
                if (val === null) {
                    return <div style={{color: 'red'}}>None Recorded</div>
                }
                return `${val} hrs`
            }
        },
        {
            title: 'Quantity',
            key: 'quantity',
            dataIndex: 'quantity'
        },
        {
            title: 'Quantity Created',
            key: 'quantityCreated',
            dataIndex: 'quantityCreated',
            render: (val) => {
                if (val === null) {
                    return <div style={{color: 'red'}}>N/A</div>
                }
                return val;
            }
        },
        {
            title: 'Wholesale',
            key: 'wholesale',
            dataIndex: 'wholesaleCost',
            render: (val, record) => {
                if (val === null) {
                    return <div style={{color: 'red'}}>N/A</div>
                }
                return `$ ${val}`
            }
        },
        {
            title: 'Supply',
            key: 'supply',
            dataIndex: 'supplyCost',
            render: (val, record) => {
                if (val === null) {
                    return <div style={{color: 'red'}}>N/A</div>
                }
                return `$ ${val}`
            }
        },
        {
            title: 'Labor',
            key: 'labor',
            dataIndex: 'hours',
            render: (val, record) => {
                if (val === null) {
                    return <div style={{color: 'red'}}>N/A</div>
                }
                const laborCost =  parseFloat(record.hours) * parseFloat(record.hourRate);
                return `$ ${laborCost.toFixed(2)}`
            }
        }
    ]

    const laborColumns = [
        {
            title: 'Notes',
            key: 'notes',
            dataIndex: 'notes',
        },
        {
            title: 'Hours',
            key: 'hours',
            dataIndex: 'hours',
        },
        {
            title: 'User',
            key: 'user',
            dataIndex: 'username',
        },
        {
            title: 'Timestamp',
            key: 'timestamp',
            dataIndex: 'timestamp',
            render: (val) => {
                return toFSDateTime(new Date(val));
            }
        }
    ]

    const poRows = Object.values(report).map(line => {
        return line.lines[0]
    })

    const cardStyle= {
        textAlign: 'left',
        margin: '5px'
    }

    return <div>
        <Card style={{textAlign: 'left', margin: "5px"}} title={"Run Purchase Order Report"}>
            <Row style={{margin: '10px'}}>
                <Col style={{marginTop: '5px', width: '80px'}}><b>Customer: </b></Col>
                <Col><BuyerSelectWidget selectedBuyer={selectedBuyer} onSelect={onBuyerSelect} value={buyerValue} setValue={setBuyerValue} type={"standard"} onApiFail={onApiFail} /></Col>
            </Row>
            <Row style={{margin: '10px'}}>
                <Col style={{marginTop: '5px', width: '80px'}}><b>Start Date: </b></Col>
                <Col><DatePickerWidget placeholder={"Start Date"} onChange={onStartDateChange} date={displayStartDate} /></Col>
            </Row>
            <Row style={{margin: '10px'}}>
                <Col style={{marginTop: '5px', width: '80px'}}><b>End Date: </b></Col>
                <Col><DatePickerWidget placeholder={"End Date"} onChange={onEndDateChange} date={displayEndDate} /></Col>
            </Row>
            <Row style={{margin: '10px'}}>
                <Col span={2}>
                    <Button onClick={runReport} disabled={disable}>Run</Button>
                </Col>
                <Col>
                    <Button onClick={clearForm}>Clear Form</Button>
                </Col>
            </Row>

        </Card>

        <Card style={cardStyle} title={<b>Summary View</b>}>
            <Table columns={poColumns} dataSource={poRows} pagination={false} />
        </Card>

        <Card style={cardStyle} title={<b>Detailed View</b>}>
            {poRows.map((row, index) => {
                return <Collapse key={index} ghost bordered={false} items={[
                    {
                        key: 1,
                        label: <Row><b>{row.poNumber}</b></Row>,
                        children:
                            <div>
                                {(laborMap[row.purchaseId] !== undefined) &&
                                <div>
                                    <Table columns={laborColumns} dataSource={laborMap[row.purchaseId]} pagination={false}/>
                                    <Divider />
                                </div>
                                }
                                <Table columns={itemColumns} dataSource={report[row.purchaseId].lines} pagination={false}/>
                            </div>
                    },
                ]} />

            })
            }
        </Card>
    </div>
}